import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Form, Select, Input, DatePicker, Button, Space, Spin, Tabs, message } from 'antd';
import { SearchOutlined, ReloadOutlined, DownloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import GrowthValue from '../../../components/GrowthValue';
import Charts from './components/Charts';
import { MaterialsTab, SuppliersTab, OrdersTab, ComparisonTab } from './components/Tables';
import { getPurchaseCostOptions, getPurchaseCostSummary, exportPurchaseCost } from '../../../services/api';
import { yuan } from './columns';

const { RangePicker } = DatePicker;

const defaultRange = () => [ dayjs().startOf('year'), dayjs() ];

function buildParams(values) {
  const params = {};
  if (values.range && values.range[0] && values.range[1]) {
    params.startDate = values.range[0].format('YYYY-MM-DD');
    params.endDate = values.range[1].format('YYYY-MM-DD');
  }
  [ 'deptName', 'purchaserName', 'supplierNumber' ].forEach(key => {
    if (values[key]) params[key] = values[key];
  });
  if (values.materialKeyword && values.materialKeyword.trim()) {
    params.materialKeyword = values.materialKeyword.trim();
  }
  return params;
}

function KpiCard({ title, value, precision, prefix, footer }) {
  return (
    <Card size="small" style={{ height: '100%' }}>
      <Statistic title={title} value={value} precision={precision} prefix={prefix} />
      {footer && <div style={{ marginTop: 4, fontSize: 12, color: '#999' }}>{footer}</div>}
    </Card>
  );
}

export default function PurchaseCost() {
  const [ form ] = Form.useForm();
  const [ options, setOptions ] = useState({ depts: [], purchasers: [], suppliers: [] });
  const [ params, setParams ] = useState(() => buildParams({ range: defaultRange() }));
  const [ summary, setSummary ] = useState(null);
  const [ loading, setLoading ] = useState(true);
  const [ exporting, setExporting ] = useState(false);
  const [ activeTab, setActiveTab ] = useState('materials');
  const [ material, setMaterial ] = useState(undefined);

  useEffect(() => {
    getPurchaseCostOptions()
      .then(res => setOptions(res.data || {}))
      .catch(err => message.error(err.message));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getPurchaseCostSummary(params)
      .then(res => { if (!cancelled) setSummary(res.data); })
      .catch(err => { if (!cancelled) message.error(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [ params ]);

  const applySearch = () => {
    setMaterial(undefined);
    setParams(buildParams(form.getFieldsValue()));
  };

  const handleReset = () => {
    form.resetFields();
    setMaterial(undefined);
    setParams(buildParams({ range: defaultRange() }));
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await exportPurchaseCost({ ...params, materialNumber: material || undefined });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `采购成本分析_${dayjs().format('YYYY-MM-DD')}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      message.error(err.message);
    }
    setExporting(false);
  };

  const kpi = (summary && summary.kpi) || {};

  const tabItems = [
    { key: 'materials', label: '物料成本汇总', children: <MaterialsTab params={params} active={activeTab === 'materials'} /> },
    { key: 'suppliers', label: '供应商汇总', children: <SuppliersTab params={params} active={activeTab === 'suppliers'} /> },
    { key: 'orders', label: '采购单明细', children: <OrdersTab params={params} active={activeTab === 'orders'} /> },
    { key: 'comparison', label: '供应商比价', children: <ComparisonTab params={params} active={activeTab === 'comparison'} material={material} onMaterialChange={setMaterial} /> },
  ];

  return (
    <div style={{ padding: 8 }}>
      <Card size="small" style={{ marginBottom: 16 }}>
        <Form form={form} layout="inline" initialValues={{ range: defaultRange() }}>
          <Form.Item name="range" label="采购日期">
            <RangePicker allowClear={false} />
          </Form.Item>
          <Form.Item name="deptName" label="采购部门">
            <Select allowClear placeholder="全部" style={{ width: 150 }} options={options.depts} />
          </Form.Item>
          <Form.Item name="purchaserName" label="采购员">
            <Select allowClear placeholder="全部" style={{ width: 130 }} options={options.purchasers} />
          </Form.Item>
          <Form.Item name="supplierNumber" label="供应商">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="全部"
              style={{ width: 240 }}
              options={options.suppliers}
            />
          </Form.Item>
          <Form.Item name="materialKeyword" label="物料">
            <Input allowClear placeholder="编码或名称关键字" style={{ width: 180 }} onPressEnter={applySearch} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" icon={<SearchOutlined />} onClick={applySearch}>查询</Button>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>重置</Button>
              <Button icon={<DownloadOutlined />} loading={exporting} onClick={handleExport}>导出 Excel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      <Spin spinning={loading}>
        <Row gutter={[ 16, 16 ]}>
          <Col xs={12} sm={8} lg={4}>
            <KpiCard
              title="采购总额"
              value={kpi.totalAmount || 0}
              precision={2}
              prefix="¥"
              footer={<span>去年同期 {yuan(kpi.lastYearAmount)} <GrowthValue value={kpi.lastYearGrowth} /></span>}
            />
          </Col>
          <Col xs={12} sm={8} lg={4}>
            <KpiCard
              title="采购单数"
              value={kpi.orderCount || 0}
              footer={<span>环比 <GrowthValue value={kpi.growth} /></span>}
            />
          </Col>
          <Col xs={12} sm={8} lg={4}>
            <KpiCard title="明细行数" value={kpi.lineCount || 0} />
          </Col>
          <Col xs={12} sm={8} lg={4}>
            <KpiCard title="物料种数" value={kpi.materialCount || 0} />
          </Col>
          <Col xs={12} sm={8} lg={4}>
            <KpiCard title="供应商数" value={kpi.supplierCount || 0} />
          </Col>
          <Col xs={12} sm={8} lg={4}>
            <KpiCard title="单均金额" value={kpi.avgOrderAmount || 0} precision={2} prefix="¥" />
          </Col>
        </Row>
        <div style={{ margin: '8px 0 16px', color: '#999', fontSize: 12 }}>
          金额口径：含税价税合计 {yuan(kpi.totalAmount)}（不含税 {yuan(kpi.amountExTax)}，税额 {yuan(kpi.taxAmount)}）；部门/采购员为空的单据归入「未填」。
        </div>

        <Charts summary={summary} />

        <Card style={{ marginTop: 16 }}>
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={tabItems}
          />
        </Card>
      </Spin>
    </div>
  );
}
